import { handleActions } from 'redux-actions';
import _findIndex from 'lodash/findIndex';

import { socketEmit, socketEvent, SOCKET_DISCONNECTED, NEW_CUSTOMER } from '../communication/actions';
import {
  OPEN_CONVERSATION,
  OPEN_CONVERSATION_SUCCESS,
  OPEN_CONVERSATION_FAILURE,
  CHANGE_CHATTER_MESSAGE_STATUS_SUCCESS,
  CLOSE_CONVERSATION,
  OPEN_CHAT,
  CLOSE_CHAT,
  CLOSE_CUSTOMER_CHAT_PENDING,
  CLOSE_CUSTOMER_CHAT_SUCCESS,
  CLOSE_CUSTOMER_CHAT_FAILURE,
  SET_CUSTOMER_AGENT,
  CHECK_CUSTOMER_AGENT,
  CHECK_CUSTOMER_AGENT_DONE,
  CLEAR_CONVERSATIONS,
} from './actions';
import { logoutSuccess, loginSuccess } from '../auth/actions';

import { findConversation } from './utils';

const defaultConversationState = {
  username: null,
  messages: [],
  oldest: null,
  hasMoreHistory: null,
  length: 0,
  noHistory: false,
  isAnonymousUser: false,
  customerChatId: null,
  customerVideoCallId: null,
  closeCustomerChatPending: false,
};

function ensureConversation(conversations, username, { silent, isAnonymousUser, customerChatId, customerVideoCallId }) {
  const conversation = findConversation(conversations, username);
  if (!conversation) {
    const newConversation = {
      ...defaultConversationState,
      username,
      messages: [],
      length: 0,
      silent,
      isAnonymousUser,
      customerChatId,
      customerVideoCallId,
    };
    return [newConversation, [newConversation, ...conversations]];
  }
  const result = [
    {
      ...conversation,
      silent,
      isAnonymousUser: typeof isAnonymousUser === 'boolean' ? isAnonymousUser : conversation.isAnonymousUser,
      customerChatId: customerChatId || conversation.customerChatId,
      customerVideoCallId: customerVideoCallId || conversation.customerVideoCallId,
    },
    ...conversations.filter(entry => entry.username !== username),
  ];
  return [result[0], result];
}

function updateMessages(state, args, silent) {
  const { id, message } = args;
  let from;
  if (args.from) {
    // eslint-disable-next-line prefer-destructuring
    from = args.from;
  } else if (args.creator) {
    from = args.creator;
  }

  if (id && from && message) {
    let { unopenedMessagesFrom } = state;
    const [currentConversation, activeConversations] = ensureConversation(state.activeConversations, from, { silent });
    const messageIndex = _findIndex(currentConversation.messages, ['id', id]);
    if (messageIndex > -1) {
      currentConversation.messages[messageIndex] = {
        ...currentConversation.messages[messageIndex],
        status: args.status,
      };
    } else {
      currentConversation.messages = [
        { ...args, cdate: args.cdate || new Date().toISOString().replace(/\.?\d*Z/, '+00:00') },
        ...currentConversation.messages,
      ];
      currentConversation.length++;
    }

    unopenedMessagesFrom = {
      ...unopenedMessagesFrom,
      [from]: {
        ...unopenedMessagesFrom[from],
        messages: currentConversation.messages.filter(
          entry => entry.creator === from && (!entry.status || entry.status !== 'seen')
        ).length,
      },
    };

    return { ...state, activeConversations, unopenedMessagesFrom };
  }
  return state;
}

function updateEmittedMessages(state, args) {
  const { to, message } = args;
  if (to && message) {
    const [currentConversation, activeConversations] = ensureConversation(state.activeConversations, to, {
      customerChatId: args.customerChat,
    });
    currentConversation.messages = [
      { id: new Date().toISOString(), cdate: new Date().toISOString().replace(/\.?\d*Z/, '+00:00'), message },
      ...currentConversation.messages,
    ];
    currentConversation.length++;
    currentConversation.hasInactivityWarning = false;

    return { ...state, activeConversations };
  }
  return state;
}

function handleSocketEmission(state, { payload: { eventName, args } }) {
  switch (eventName) {
    case 'message':
      return updateEmittedMessages(state, args);
  }
  return state;
}

function handleSocketEvents(state, { payload: { eventName, args } }) {
  switch (eventName) {
    case 'chat_inactivity_warning':
      return {
        ...state,
        activeConversations: state.activeConversations.map(conversation =>
          conversation.username === args.from ? { ...conversation, hasInactivityWarning: true } : conversation
        ),
      };
    case 'message':
      return updateMessages(state, args, true);
  }
  return state;
}

function handleOpenConversation(
  state,
  { payload: { username, history, isAnonymousUser, customerChatId, customerVideoCallId } }
) {
  if (username) {
    let { unopenedMessagesFrom } = state;
    const [currentConversation, activeConversations] = ensureConversation(state.activeConversations, username, {
      isAnonymousUser,
      customerChatId,
      customerVideoCallId,
    });
    currentConversation.messages = history;
    currentConversation.silent = false;

    unopenedMessagesFrom = { ...unopenedMessagesFrom };
    if (unopenedMessagesFrom[username]) {
      unopenedMessagesFrom[username] = {
        ...unopenedMessagesFrom[username],
        messages: currentConversation.messages.filter(
          entry => entry.creator === username && (!entry.status || entry.status !== 'seen')
        ).length,
      };
    }

    return {
      ...state,
      activeConversations,
      unopenedMessagesFrom,
      error: null,
      pending: false,
    };
  }
  return state;
}

export default handleActions(
  {
    [OPEN_CHAT]: (state, { payload: { username } }) => ({ ...state, open: true, activeConversationWith: username }),
    [CLOSE_CHAT]: state => ({ ...state, open: false, activeConversationWith: null }),
    [OPEN_CONVERSATION]: state => ({ ...state, pending: true }),
    [OPEN_CONVERSATION_SUCCESS]: (state, action) => ({ ...handleOpenConversation(state, action), pending: false }),
    [OPEN_CONVERSATION_FAILURE]: (state, { payload: { error } }) => ({ ...state, error, open: false, pending: false }),
    [CLOSE_CONVERSATION]: state => ({
      ...state,
      activeConversations: [
        ...state.activeConversations.filter(entry => entry.username !== state.activeConversationWith),
      ],
    }),
    [CHANGE_CHATTER_MESSAGE_STATUS_SUCCESS]: (state, { payload }) => {
      return updateMessages(state, payload);
    },
    [socketEmit]: handleSocketEmission,
    [socketEvent]: handleSocketEvents,
    [CLOSE_CUSTOMER_CHAT_PENDING]: (state, { payload: username }) => ({
      ...state,
      activeConversations: state.activeConversations.map(conv =>
        conv.username === username ? { ...conv, closeCustomerChatPending: true } : conv
      ),
    }),
    [CLOSE_CUSTOMER_CHAT_SUCCESS]: (state, { payload: username }) => ({
      ...state,
      activeConversations: state.activeConversations.filter(conv => conv.username !== username),
    }),
    [CLOSE_CUSTOMER_CHAT_FAILURE]: (state, { payload: username }) => ({
      ...state,
      activeConversations: state.activeConversations.map(conv =>
        conv.username === username ? { ...conv, closeCustomerChatPending: false } : conv
      ),
    }),
    [CHECK_CUSTOMER_AGENT]: state => ({ ...state, checkAgentPending: true }),
    [CHECK_CUSTOMER_AGENT_DONE]: state => ({ ...state, checkAgentPending: false }),
    [SET_CUSTOMER_AGENT]: (state, { payload: agent }) => ({
      ...state,
      agent,
      checkAgentPending: agent ? false : state.checkAgentPending,
    }),
    [SOCKET_DISCONNECTED]: state => {
      return {
        ...state,
        activeConversations: state.activeConversations.filter(conversation => !conversation.hasInactivityWarning),
      };
    },
    [NEW_CUSTOMER]: (
      state,
      { payload: { username, isAnonymousUser, customerChat: customerChatId, customerVideoCall: customerVideoCallId } }
    ) => {
      const [currentConversation, activeConversations] = ensureConversation(state.activeConversations, username, {
        isAnonymousUser,
        customerChatId,
        customerVideoCallId,
      });
      currentConversation.silent = false;

      return {
        ...state,
        activeConversations,
        error: null,
        pending: false,
      };
    },
    [CLEAR_CONVERSATIONS]: state => ({
      ...state,
      activeConversationWith: null,
      activeConversations: [],
    }),
    [logoutSuccess]: state => ({
      ...state,
      open: false,
      activeConversationWith: null,
      activeConversations: [],
      unopenedMessagesFrom: {},
      reviewData: null,
      leaveReview: false,
      pending: false,
      error: null,
      agent: null,
    }),
    [loginSuccess]: state => ({
      ...state,
      open: false,
      activeConversationWith: null,
      activeConversations: [],
      unopenedMessagesFrom: {},
      reviewData: null,
      leaveReview: false,
      pending: false,
      error: null,
      agent: null,
    }),
  },
  {
    open: false,
    activeConversationWith: null,
    activeConversations: [],
    unopenedMessagesFrom: {},
    reviewData: null,
    leaveReview: false,
    pending: false,
    error: null,
    checkAgentPending: false,
    agent: null,
  }
);
